<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div v-if="coordinates">
    <a href="#" class="location-link" @click.prevent="showMap">
      {{ coordinates }}
    </a>
    <oxd-dialog v-if="isMapVisible" @update:show="closeMap">
      <div class="location-modal">
        <h3 class="location-modal-title">{{ title }}</h3>
        <div ref="mapContainer" class="location-map"></div>
        <div class="location-modal-actions">
          <oxd-button display-type="ghost" label="Close" @click="closeMap" />
          <oxd-button
            display-type="secondary"
            label="Open in Google Maps"
            @click="openInGoogleMaps"
          />
        </div>
      </div>
    </oxd-dialog>
  </div>
  <div v-else>--</div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {OxdDialog} from '@ohrm/oxd';

export default {
  name: 'LocationCell',
  components: {
    'oxd-dialog': OxdDialog,
  },
  props: {
    coordinates: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: 'Location',
    },
  },
  data() {
    return {
      isMapVisible: false,
      map: null,
    };
  },
  methods: {
    showMap() {
      this.isMapVisible = true;
      this.$nextTick(() => {
        this.initMap();
      });
    },
    closeMap() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.isMapVisible = false;
    },
    initMap() {
      if (!this.coordinates || this.map) return;

      const [lat, lng] = this.coordinates
        .split(',')
        .map((c) => parseFloat(c.trim()));

      if (isNaN(lat) || isNaN(lng)) return;

      this.map = L.map(this.$refs.mapContainer).setView([lat, lng], 15);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(this.map);

      // Fix marker icon paths
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: '/HR/web/dist/images/marker-icon-2x.png',
        iconUrl: '/HR/web/dist/images/marker-icon.png',
        shadowUrl: '/HR/web/dist/images/marker-shadow.png',
      });

      L.marker([lat, lng])
        .addTo(this.map)
        .bindPopup(`<b>${this.title}</b><br>Lat: ${lat}<br>Lng: ${lng}`)
        .openPopup();

      // Force map to resize properly in modal
      setTimeout(() => {
        this.map.invalidateSize();
      }, 100);
    },
    openInGoogleMaps() {
      if (!this.coordinates) return;
      const [lat, lng] = this.coordinates
        .split(',')
        .map((c) => parseFloat(c.trim()));
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    },
  },
};
</script>

<style scoped lang="scss">
.location-link {
  color: #ff7b1c;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.location-modal {
  width: 100%;
  max-width: 600px;
  padding: 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
}

.location-modal-title {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 16px;
    margin: 0 0 12px 0;
  }
}

.location-map {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    height: 300px;
  }

  @media (max-width: 480px) {
    height: 250px;
    margin-bottom: 12px;
  }
}

.location-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;

    button {
      width: 100%;
    }
  }
}
</style>
